import type { Address as FormAddress } from '@brightdrop/bd-form';
import type { Address } from '@gm-commercial/location-model';
import type { User } from '@gm-commercial/profile-model';
import type {
  MexicoFiscalDetails,
  OrganizationDto,
  OrganizationResponseV20241120,
  OrganizationUpdateRequestV20241204,
} from '@gm-commercial/rtk-clients/organizations/types';

import type { ApiAddressFields } from '../mappers/common.mappers';
import type {
  ActivationStatus,
  Entity,
  Location,
  PartialEntity,
  SummaryCounts,
} from './common.model';

export enum OrgActivationStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  ENROLLING = 'ENROLLING',
  ABORTED = 'ABORTED',
  CLOSING = 'CLOSING',
  CLOSED = 'CLOSED',
}

// TODO: replace with generated API types once the API changes are integrated
export const VerificationStatus = {
  PARTIALLY_VERIFIED: 'PARTIALLY_VERIFIED',
  VERIFIED: 'VERIFIED',
  UNVERIFIED: 'UNVERIFIED',
} as const;

export type VerificationStatusType =
  (typeof VerificationStatus)[keyof typeof VerificationStatus];

/**
 * Canonical verificationStatusReasonCode union, sourced from the generated
 * organizations client so it stays in sync with the API contract.
 */
export type VerificationStatusReasonCodeType = NonNullable<
  OrganizationResponseV20241120['verificationStatusReasonCode']
>;

/** Runtime handles for the verification reason codes the advisor UI messages on. */
export const VerificationStatusReasonCode = {
  FAN_VERIFICATION_INCOMPLETE: 'FAN_VERIFICATION_INCOMPLETE',
  DOMAIN_MISMATCH: 'DOMAIN_MISMATCH',
  FAN_NOT_FOUND: 'FAN_NOT_FOUND',
  FAN_NOT_ELIGIBLE: 'FAN_NOT_ELIGIBLE',
  FAN_READY_TO_LINK: 'FAN_READY_TO_LINK',
} as const satisfies Record<string, VerificationStatusReasonCodeType>;

/**
 * Tokens carried in verificationStatusReasonNote that tell apart the
 * FAN_NOT_ELIGIBLE cases. The API types the note as a free-form string.
 */
export const FanNotEligibleReasonNote = {
  GOV_FMC: 'GOV_FMC',
  PARENT_CHILD: 'PARENT_CHILD',
  CUSTOMER_LINKAGE: 'CUSTOMER_LINKAGE',
} as const;

export type FanNotEligibleReasonNoteType =
  (typeof FanNotEligibleReasonNote)[keyof typeof FanNotEligibleReasonNote];

export enum Industry {
  AGRICULTURE = 'AGRICULTURE',
  MINING = 'MINING',
  UTILITIES = 'UTILITIES',
  CONSTRUCTION = 'CONSTRUCTION',
  MANUFACTURING = 'MANUFACTURING',
  WHOLESALE = 'WHOLESALE',
  RETAIL = 'RETAIL',
  TRANSPORTATION = 'TRANSPORTATION',
  INFORMATION = 'INFORMATION',
  FINANCE = 'FINANCE',
  REAL_ESTATE = 'REAL_ESTATE',
  PROFESSIONAL = 'PROFESSIONAL',
  MANAGEMENT = 'MANAGEMENT',
  ADMINISTRATIVE = 'ADMINISTRATIVE',
  EDUCATIONAL = 'EDUCATIONAL',
  HEALTH = 'HEALTH',
  ARTS = 'ARTS',
  FOOD = 'FOOD',
  OTHER = 'OTHER',
  PUBLIC = 'PUBLIC',
}

export enum DeclineReason {
  NOT_QUALIFIED = 'NOT_QUALIFIED',
  OTHER = 'OTHER',
}

/**
 * Canonical reviewReason union, sourced from the generated organizations client
 * so it stays in sync with the API contract (every endpoint shares this union).
 */
export type ReviewReasonType = NonNullable<
  OrganizationResponseV20241120['reviewReason']
>;

/**
 * Runtime handles for the reviewReasons the advisor UI references directly.
 * `satisfies` guarantees each value is a valid `ReviewReasonType`, so a typo or
 * a value dropped from the API union fails to compile here.
 */
export const ReviewReason = {
  CUSTOMER_DECLINED: 'CUSTOMER_DECLINED',
  DOMAIN_MISMATCH: 'DOMAIN_MISMATCH',
  FAN_NOT_ELIGIBLE: 'FAN_NOT_ELIGIBLE',
  FAN_DUPLICATE: 'FAN_DUPLICATE',
  FAN_NOT_FOUND: 'FAN_NOT_FOUND',
  NO_MATCH: 'NO_MATCH',
  VERIFICATION_UNAVAILABLE: 'VERIFICATION_UNAVAILABLE',
} as const satisfies Record<string, ReviewReasonType>;

/**
 * Canonical draftReasonCode union, sourced from the generated organizations client.
 */
export type DraftReasonCodeType = NonNullable<
  OrganizationDto['draftReasonCode']
>;

export const DraftReasonCode = {
  MIGRATION: 'MIGRATION',
} as const satisfies Record<string, DraftReasonCodeType>;

export type OrgDeclineForm = {
  reasonCode: DeclineReason.NOT_QUALIFIED | DeclineReason.OTHER | null;
  reasonNote: string | null;
};

export type EnrollmentCandidate = {
  candidateRef: string;
  name: string;
  address: ApiAddressFields;
  fan: string | null;
  phoneNumber: string | null;
  website: string | null;
  industry: string | null;
};

export type EnrollmentLookupResult = {
  enrollmentCandidates: EnrollmentCandidate[];
};

export type OrganizationTerms = {
  accepted: boolean;
  url: {
    termsAndConditions: string;
  };
  acceptedDate: string;
};

export interface Organization extends Entity {
  phoneNumber: string;
  email: string;
  status: ActivationStatus;
  address?: Address;
  creationDate?: string;
  activationDate?: string;
  primaryContact?: PartialEntity<User>;
  location?: Location;
  summaryCounts?: SummaryCounts;
  orgType?: OrganizationType;
  orgMasterNumber?: string;
}

type OrgAutoOnboardingConfiguration = {
  autoImportOrdersEnabled: boolean;
  autoOnboardVehiclesEnabled: boolean;
  autoMoveVehiclesToFleetEnabled: boolean;
};

type OrgAutoOnboardingEffectiveSettings = {
  canAutoImportOrders: boolean;
  canAutoOnboardVehicles: boolean;
  canAutoMoveVehiclesToFleet: boolean;
};

export type OrgSettings = {
  autoOnboarding: {
    configuration: OrgAutoOnboardingConfiguration;
    effectiveSettings: OrgAutoOnboardingEffectiveSettings;
    hasOpenedModal: boolean;
  };
};

export interface OrganizationItem extends Entity {
  address: ApiAddressFields;
  industry: Industry;
  emailAddress: string;
  status: OrgActivationStatus;
  verificationStatus?: VerificationStatusType;
  /** Explains the verification status; absent for VERIFIED orgs */
  verificationStatusReasonCode?: VerificationStatusReasonCodeType;
  /** Refines the reason code, e.g. which FAN_NOT_ELIGIBLE case applies */
  verificationStatusReasonNote?: string;
  /** Present on ENROLLING orgs awaiting admin review */
  reviewReason?: ReviewReasonType;
  /** Present on DRAFT orgs (e.g. OVI migration) */
  draftReasonCode?: DraftReasonCodeType;
  draftReasonMessage?: string;
  creationDate?: string;
  activationDate?: string;
  primaryContact?: PartialEntity<User>;
  organizationId?: string;
  preferredName?: string;
  website?: string;
  fan?: string;
  /** Federal tax identifier captured at enrollment; absent when the org has none */
  taxId?: string;
  /** Analytics classification; absent until an advisor sets one */
  orgType?: OrgClassification;
  /** Market the organization operates in; US when absent */
  countryOfOperation?: OrganizationResponseV20241120['countryOfOperation'];
  /** SAT fiscal identity; only Mexican organizations have one */
  mexicoFiscalDetails?: MexicoFiscalDetails | null;
  defaultFleet?: string;
  accountId?: string;
  terms?: OrganizationTerms;
  settings?: OrgSettings;
}

/** Raw shape returned by GET /organizations/:version/:id */
export type ApiOrganizationItem = Omit<
  OrganizationItem,
  'address' | 'status' | 'primaryContact'
> & {
  address: ApiAddressFields | null;
  status: string;
  primaryContact: PartialEntity<User> | null;
};
export type OrganizationTenant = Entity;

export type ApiOrganization = Partial<
  Omit<Organization, 'address' | 'status' | 'primaryContact'>
> & {
  address: ApiAddressFields | null;
  primaryContact: PartialEntity<User> | null;
} & Record<string, unknown>;

export enum OrganizationType {
  ASSET = 'ASSET',
  SERVICE = 'SERVICE',
}

/**
 * Analytics classification from the organizations client's `orgType`.
 * Distinct from {@link OrganizationType}, which is the asset/service kind.
 */
export type OrgClassification = NonNullable<
  OrganizationUpdateRequestV20241204['orgType']
>;

export type OrgFormFields = {
  name: string | null;
  preferredName: string | null;
  website: string | null;
  industry: Industry | null;
  fan: string | null;
  taxId: string | null;
  orgType: OrgClassification | null;
  address: FormAddress | null;
  primaryContact: PartialEntity<User> | null;
  onBehalfOfEmail: string | null;
};
export type OrgFormErrors = { [k in keyof OrgFormFields]: string };

export type OrgForm = {
  id?: string;
} & OrgFormFields & { errors: OrgFormErrors };

type CountSummary = {
  numberOfAssets?: number;
};

type FleetAssetCountSummary = {
  fleetId?: string;
} & CountSummary;

type AssetCountSummaryCommonAttributes = {
  organizationId: string;
  numberOfAssets?: number;
  fleets?: {
    [key: string]: FleetAssetCountSummary;
  };
};

export type ApiAssetCountSummary = AssetCountSummaryCommonAttributes;
export type AssetCountSummary = AssetCountSummaryCommonAttributes;
